import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { HeartPulse, Loader2, Check, X } from "lucide-react";
import TelehealthPipeline from "@/components/TelehealthPipeline";
import { AttorneyMedicalRecords } from "@/components/MedicalRecords";

const TABS = [["overview", "Telehealth Overview"], ["pipeline", "Client Pipeline"], ["records", "Medical Records"],
  ["pairing", "Automation & Pairing"],
  ["clients", "Telehealth Clients"], ["directory", "Medical Network"], ["audit", "Activity & Audit"]];

const Toggle = ({ label, checked, onChange, testid, hint }) => (
  <label className="flex items-start justify-between gap-3 py-2.5 cursor-pointer border-b border-slate-50 last:border-0">
    <span className="text-sm text-slate-600">{label}{hint && <span className="block text-[11px] text-slate-400">{hint}</span>}</span>
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative w-10 h-6 rounded-full shrink-0 transition-colors ${checked ? "bg-cobalt" : "bg-slate-200"}`}>
      <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[16px]" : ""}`} />
    </button>
  </label>
);

export const AttorneyTelehealth = ({ onClose }) => {
  const [tab, setTab] = useState("overview");
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [offer, setOffer] = useState(null); // provider being offered a client
  const [myCases, setMyCases] = useState([]);
  const [offerCase, setOfferCase] = useState("");

  const load = useCallback(() => {
    api.get("/attorney/telehealth/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/attorney/telehealth/clients").then(({ data }) => setClients(data)).catch(() => {});
    api.get("/attorney/telehealth/directory").then(({ data }) => setDirectory(data)).catch(() => {});
    api.get("/attorney/claimed").then(({ data }) => setMyCases(data)).catch(() => setMyCases([]));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/attorney/telehealth/settings", settings); setSettings(data); toast.success("Telehealth settings saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const sendOffer = async () => {
    if (!offerCase) { toast.error("Select a represented case"); return; }
    try {
      await api.post("/attorney/telehealth/offer", { case_id: offerCase, provider_id: offer.provider_id });
      toast.success("Offer created. If the client hasn't consented yet, they'll be asked before anything is shared.");
      setOffer(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create offer"); }
  };

  if (!settings) return <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="attorney-telehealth">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><HeartPulse className="w-5 h-5 text-cobalt" /><h2 className="font-heading font-bold text-navy">Telehealth & Medical Network</h2></div>
          <button onClick={onClose} data-testid="attorney-telehealth-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`ath-tab-${k}`}
              className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap ${tab === k ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{label}</button>
          ))}
        </div>

        <div className="mt-5">
          {tab === "overview" && (
            <div className="grid sm:grid-cols-3 gap-3" data-testid="ath-overview">
              {[["Workflow", settings.enabled ? "Enabled" : "Disabled"],
                ["Auto pairing", settings.auto_pairing ? "On" : "Off (default)"],
                ["Active telehealth clients", clients.filter((c) => c.status === "accepted").length],
                ["Awaiting consent", clients.filter((c) => c.status === "awaiting_consent").length],
                ["Offered to providers", clients.filter((c) => c.status === "offered").length],
                ["Approved network providers", directory.length]].map(([label, val]) => (
                <div key={label} className="rounded-2xl bg-slate-50 px-4 py-3">
                  <div className="font-heading text-lg font-extrabold text-navy">{String(val)}</div>
                  <div className="text-[11px] text-slate-500">{label}</div>
                </div>
              ))}
              <p className="sm:col-span-3 text-[11px] text-slate-400">Crash Ready is the coordination platform only. Medical services are performed by independent licensed providers; legal decisions remain with your firm. No client information transmits without an executed representation agreement, your enabled workflow, and the client's consent.</p>
            </div>
          )}

          {tab === "pipeline" && <TelehealthPipeline reloadParent={load} />}

          {tab === "records" && <AttorneyMedicalRecords />}

          {tab === "pairing" && (
            <div data-testid="ath-pairing">
              <div className="rounded-2xl border border-slate-200 p-4 mb-3" data-testid="ath-automation-mode">
                <div className="text-[11px] font-semibold text-slate-500 uppercase mb-2">Automation mode</div>
                {[["manual", "Manual", "I select 'Send to Telehealth Coordination' for each client."],
                  ["prepare", "Prepare for approval", "The system prepares the referral and recommended provider — I review and approve every transmission."],
                  ["auto", "Automatic after conditions", "Send automatically once retainer, client authorization, my settings, and all conditions pass with no safety block."]].map(([k, label, hint]) => (
                  <label key={k} className="flex items-start gap-2 py-1.5 cursor-pointer">
                    <input type="radio" name="automation_mode" checked={(settings.automation_mode || "manual") === k}
                      onChange={() => { set("automation_mode", k); if (k !== "manual") set("enabled", true); }}
                      data-testid={`ath-mode-${k}`} className="mt-0.5 accent-cobalt" />
                    <span className="text-sm text-slate-700 font-medium">{label}<span className="block text-[11px] text-slate-400 font-normal">{hint}</span></span>
                  </label>
                ))}
              </div>
              <div className="rounded-2xl border border-slate-200 p-4">
                <Toggle testid="ath-auto-pairing" checked={settings.auto_pairing}
                  onChange={(v) => { set("auto_pairing", v); if (v) set("enabled", true); }}
                  label="Automatically offer authorized clients to an approved telehealth provider after the representation agreement is signed."
                  hint="Default OFF. Even when enabled, transmission still requires client consent and every configured condition below." />
              </div>
              <div className="mt-3 rounded-2xl border border-slate-100 p-4">
                <Toggle testid="ath-enabled" checked={settings.enabled} onChange={(v) => set("enabled", v)} label="Enable telehealth workflow for my firm" />
                <Toggle testid="ath-manual-approval" checked={settings.require_manual_approval} onChange={(v) => set("require_manual_approval", v)} label="Require my manual approval before any transmission" />
                <Toggle testid="ath-auto-matching" checked={settings.allow_auto_matching} onChange={(v) => set("allow_auto_matching", v)} label="Allow automatic provider matching" />
                <Toggle testid="ath-rn-approval" checked={settings.require_rn_approval} onChange={(v) => set("require_rn_approval", v)} label="Require RN approval before telehealth referral" />
                <Toggle testid="ath-high-risk" checked={settings.high_risk_manual_confirmation} onChange={(v) => set("high_risk_manual_confirmation", v)} label="Require another manual confirmation for high-risk cases" />
                <Toggle testid="ath-office-hours" checked={settings.office_hours_only} onChange={(v) => set("office_hours_only", v)} label="Only during office hours" />
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Preferred provider</label>
                  <select value={settings.preferred_provider_id || ""} onChange={(e) => set("preferred_provider_id", e.target.value || null)} data-testid="ath-preferred"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                    <option value="">No preference</option>
                    {directory.map((d) => <option key={d.provider_id} value={d.provider_id}>{d.practice_name}</option>)}
                  </select></div>
                <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Max distance (miles)</label>
                  <input type="number" value={settings.max_distance_miles} onChange={(e) => set("max_distance_miles", Number(e.target.value))} data-testid="ath-distance"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></div>
                <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Covered states (comma-sep, empty = all)</label>
                  <input value={(settings.covered_states || []).join(", ")} onChange={(e) => set("covered_states", e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean))} data-testid="ath-states"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="TX, FL" /></div>
                <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Exclusions (accident types, comma-sep)</label>
                  <input value={(settings.exclusions || []).join(", ")} onChange={(e) => set("exclusions", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="ath-exclusions"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="pedestrian, commercial" /></div>
                <div><label className="text-[11px] font-semibold text-slate-500 uppercase">Provider response timer (hours)</label>
                  <input type="number" value={settings.response_timer_hours ?? 48} onChange={(e) => set("response_timer_hours", Number(e.target.value))} data-testid="ath-response-timer"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" /></div>
                <div className="flex items-end pb-1"><label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={!!settings.auto_reoffer} onChange={(e) => set("auto_reoffer", e.target.checked)} data-testid="ath-auto-reoffer" className="accent-cobalt" />
                  Auto re-offer to the next provider after expiry</label></div>
              </div>
              <button onClick={save} disabled={saving} data-testid="ath-save" className="mt-4 px-6 py-2.5 rounded-full bg-navy text-white text-sm font-semibold disabled:opacity-50">{saving ? "Saving…" : "Save telehealth settings"}</button>
            </div>
          )}

          {tab === "clients" && (
            clients.length === 0 ? <div className="py-10 text-center text-sm text-slate-400" data-testid="ath-clients-empty">No telehealth activity yet. Offer a represented client from the Medical Network tab.</div> :
            <div className="divide-y divide-slate-50" data-testid="ath-clients">
              {clients.map((c) => (
                <div key={c.request_id} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div>
                    <span className="font-mono text-xs text-slate-400">{c.case_number}</span>
                    <span className="ml-2 text-navy font-medium">{c.provider_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{c.status.replace(/_/g, " ")}</span>
                    {["awaiting_consent", "offered"].includes(c.status) && (
                      <button onClick={async () => { await api.post(`/attorney/telehealth/requests/${c.request_id}/withdraw`); toast.success("Offer withdrawn"); load(); }}
                        data-testid={`ath-withdraw-${c.request_id}`} className="text-xs text-slate-400 hover:text-red-500">Withdraw</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "directory" && (
            directory.length === 0 ? <div className="py-10 text-center text-sm text-slate-400" data-testid="ath-directory-empty">No approved telehealth providers in the network yet.</div> :
            <div className="grid sm:grid-cols-2 gap-3" data-testid="ath-directory">
              {directory.map((d) => (
                <div key={d.provider_id} className="rounded-2xl border border-slate-100 p-4">
                  <div className="font-semibold text-navy text-sm flex items-center gap-1.5">{d.practice_name}{d.verified_by_admin && <Check className="w-3.5 h-3.5 text-emerald-500" />}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{(d.provider_type || "").replace(/_/g, " ")} · {(d.states || []).join(", ") || "all states"}</div>
                  {d.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{d.description}</p>}
                  <button onClick={() => setOffer(d)} data-testid={`ath-offer-${d.provider_id}`}
                    className="mt-3 px-4 py-1.5 rounded-full bg-cobalt text-white text-xs font-semibold">Offer a client</button>
                </div>
              ))}
            </div>
          )}

          {tab === "audit" && <AuditList />}
        </div>

        {offer && (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setOffer(null)}>
            <div className="bg-white rounded-3xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="ath-offer-modal">
              <h3 className="font-heading font-bold text-navy">Offer a client to {offer.practice_name}</h3>
              <p className="text-xs text-slate-400 mt-1">Only represented clients with a signed retainer are eligible. The client must consent before any information is shared.</p>
              <select value={offerCase} onChange={(e) => setOfferCase(e.target.value)} data-testid="ath-offer-case"
                className="mt-4 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                <option value="">Select a represented case…</option>
                {myCases.map((c) => <option key={c.case_id} value={c.case_id}>{c.case_number || c.case_id} — {c.title || c.client_name || ""}</option>)}
              </select>
              <div className="mt-4 flex gap-2 justify-end">
                <button onClick={() => setOffer(null)} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-600">Cancel</button>
                <button onClick={sendOffer} data-testid="ath-offer-send" className="px-5 py-2 rounded-full bg-navy text-white text-sm font-semibold">Create offer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AuditList = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/attorney/telehealth/clients").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (rows === null) return <Loader2 className="w-5 h-5 animate-spin text-cobalt" />;
  if (!rows.length) return <div className="py-10 text-center text-sm text-slate-400">No telehealth activity recorded yet.</div>;
  return (
    <div className="divide-y divide-slate-50" data-testid="ath-audit">
      {rows.map((r) => (
        <div key={r.request_id} className="py-2 text-xs flex justify-between gap-2">
          <span className="text-navy">{r.case_number} → {r.provider_name} <span className="text-slate-400">({r.status.replace(/_/g, " ")})</span></span>
          <span className="text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

export default AttorneyTelehealth;
